import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useQueryApiClient';
import { detectGroup } from './utils';
import { Pill } from './style';
import { NOTIFICATION_GROUPS } from './types';
import type { NotificationTypeTagProps, NotificationGroup , EnumItem} from './types';

function normalizeEnumValues(resp: any): string[] {
  const raw = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
  return (raw as EnumItem[])
    .map((x) => x?.value)
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
}

const PREFIX_TO_GROUP: Record<string, NotificationGroup> = {
  order: 'orders',
  fulfillment: 'orders',
};

const KNOWN_GROUPS = new Set<string>(NOTIFICATION_GROUPS);

const isNotificationGroup = (value: string): value is NotificationGroup => KNOWN_GROUPS.has(value);

function groupFromTypeUsingEnum(
  typeLike: string | undefined,
  enumValues: string[]
): NotificationGroup {
  const t = (typeLike || '').toLowerCase();
  if (!t) return 'other';

  const prefixes = Array.from(
    new Set(
      enumValues
        .map((v) => v.split('.')[0]?.toLowerCase())
        .filter(Boolean)
    )
  );

  for (let i = 0; i < prefixes.length; i++) {
    const p = prefixes[i]!;
    if (t.startsWith(`${p}.`)) {
      return PREFIX_TO_GROUP[p] ?? (isNotificationGroup(p) ? p : 'other');
    }
  }
  return 'other';
}

function NotificationTypeTag(props: NotificationTypeTagProps) {
  const { shortType, fallbackType, group, size = 'md', className, enumValues: enumValuesProp } = props;
  const intl = useIntl();

  const { data: enumResp } = useQueryApiClient({
    request: { url: 'api/v2/enums/NotificationType', method: 'GET', disableOnMount: true },
    enabled: !enumValuesProp,
  });

  const enumValues = useMemo(() => {
    if (enumValuesProp) return enumValuesProp;
    return normalizeEnumValues(enumResp);
  }, [enumValuesProp, enumResp]);

  const computed = useMemo(() => {
    const byEnum = groupFromTypeUsingEnum(shortType ?? fallbackType, enumValues);
    const g: NotificationGroup =
      group ?? (byEnum !== 'other' ? byEnum : detectGroup(shortType, fallbackType));

    const label =
      g !== 'other'
        ? intl.formatMessage({ id: `notifications.enums.NotificationTypeGroup.${g}` })
        : (shortType || fallbackType || '-');

    return { group: g, label };
  }, [group, shortType, fallbackType, enumValues, intl]);

  return (
    <Pill className={className} $size={size} $group={computed.group}>
      {computed.label}
    </Pill>
  );
}

export default NotificationTypeTag;
export * from './types';
export { normalizeEnumValues };
